import Actions from 'redux-actions-class'
import { browserHistory } from 'react-router'

import * as util from '../../util'
import config from '../../config'
import { alert } from '../alert'

const uhOh = {
  show: true,
  kind: 'danger',
  title: 'Uh-Oh!',
  message:
    "We're sorry, something went wrong. Please try again or contact us if this error persists.",
  selfDestruct: true,
}

const success = ({ title, message }) => ({
  show: true,
  kind: 'success',
  selfDestruct: true,
  title,
  message,
})

const createResponseActions = (name, successArg) => ({
  [`AUTH_${name}_SUCCESS`]: successArg,
  [`AUTH_${name}_FAILURE`]: 'e',
})

export default function createActions(auth) {
  const actions = new Actions({
    AUTH_INVALIDATE: null,
    AUTH_SET_USER_DATA: 'userData',
    AUTH_SET_IMPERSONATED_USER: 'impersonatedUser',

    AUTH_LOGIN: userData => async dispatch => {
      try {
        const loginData = await auth.login(userData)
        dispatch(actions.authSetUserData(userData))
        dispatch(actions.authLoginSuccess(loginData))
      } catch (e) {
        if (hasServerErrorMessage(e)) {
          dispatch(actions.authLoginFailure(await e.json()))
        } else {
          dispatch(actions.authLoginFailure(e))
        }
      }

      function hasServerErrorMessage(e) {
        return (
          e instanceof Response &&
          e.headers.get('content-type').includes('json')
        )
      }
    },

    AUTH_LOGIN_SUCCESS: 'loginData',

    AUTH_LOGIN_FAILURE: e =>
      alert({
        show: true,
        kind: 'danger',
        title: 'Uh-Oh!',
        selfDestruct: true,
        message:
          e.message ||
          'There was an error, please check your email and password.',
      }),

    AUTH_SECOND_FACTOR: code => async dispatch => {
      try {
        const loginData = await auth.secondFactor(code)
        dispatch(actions.authLoginSuccess(loginData))
      } catch (e) {
        dispatch(actions.authSecondFactorFailure())
      }
    },
    AUTH_SECOND_FACTOR_FAILURE: () =>
      alert({
        show: true,
        kind: 'danger',
        title: 'Invalid Verification Code',
        selfDestruct: true,
        message: 'You have entered an invalid or expired verification code.',
      }),

    AUTH_USER: () => async dispatch => {
      try {
        const user = await auth.user()
        dispatch(actions.authUserSuccess(user))
      } catch (e) {
        dispatch(actions.authUserFailure(e))
      }
    },
    ...createResponseActions('USER', 'user'),

    AUTH_LOGOUT: () => async dispatch => {
      try {
        await auth.logout()
        dispatch(actions.authInvalidate())
        dispatch(actions.authLogoutSuccess())
      } catch (e) {
        dispatch(actions.authLogoutFailure())
      }
    },
    AUTH_LOGOUT_SUCCESS: () =>
      alert(
        success({
          title: 'Success!',
          message: 'You successfully logged out.',
        })
      ),
    AUTH_LOGOUT_FAILURE: () => async dispatch => {
      dispatch(actions.authInvalidate())
      browserHistory.push('/login')
    },

    AUTH_RUN: () => async dispatch => {
      try {
        const user = await auth.run()
        if (user.verified) {
          dispatch(actions.authUserSuccess(user))
          const impersonatedUser = auth.getImpersonatedUser()
          if (impersonatedUser) {
            dispatch(actions.authSetImpersonatedUser(impersonatedUser))
          }
        }
      } catch (e) {
        dispatch(actions.authRunFailure(e))
      }
    },
    AUTH_RUN_FAILURE: () => async dispatch => {
      await auth.logout()
      dispatch(actions.authInvalidate())
    },

    AUTH_RESET_PASSWORD: email => async dispatch => {
      try {
        await auth.resetPassword(email)
        dispatch(actions.authResetPasswordSuccess())
      } catch (e) {
        dispatch(actions.authResetPasswordFailure())
      }
    },
    AUTH_RESET_PASSWORD_SUCCESS: () =>
      alert(
        success({
          title: 'Sent!',
          message: 'You should receive your reset password email shortly.',
        })
      ),
    AUTH_RESET_PASSWORD_FAILURE: () => alert(uhOh),

    AUTH_UPDATE_PASSWORD: ({ token, password }) => async dispatch => {
      try {
        await auth.updatePassword({ token, password })
        dispatch(actions.authUpdatePasswordSuccess())
        dispatch(actions.authSetPasswordResetSuccessful(true))
      } catch (e) {
        dispatch(actions.authUpdatePasswordFailure(e))
      }
    },
    AUTH_SET_PASSWORD_RESET_SUCCESSFUL: 'passwordResetSuccessful',
    AUTH_UPDATE_PASSWORD_SUCCESS: () =>
      alert(
        success({
          title: 'Password Reset!',
          message: "You've successfully reset your password.",
        })
      ),
    AUTH_UPDATE_PASSWORD_FAILURE: () => alert(uhOh),

    AUTH_SEND_VERIFICATION: email => async dispatch => {
      try {
        const data = await auth.sendVerification(email)
        if (data && data.sent) {
          alert({
            show: true,
            kind: 'success',
            title: 'Sent!',
            message: 'You should receive your verification email shortly.',
            selfDestruct: true,
          })(dispatch)
        } else {
          alert(uhOh)(dispatch)
        }
      } catch (err) {
        alert(uhOh)(dispatch)
      }
    },

    AUTH_VERIFY: token => async dispatch => {
      try {
        const data = await auth.verify(token)
        const alertData = {
          show: true,
          kind: 'success',
          title: 'Success!',
          selfDestruct: true,
        }

        if (data.alreadyVerified) {
          alertData.message = 'Email account already verified.'
        } else {
          alertData.message = 'Email verification successful.'
        }

        alert(alertData)(dispatch)
      } catch (err) {
        console.error('AUTH_VERIFY >> err:', err)
        alert(uhOh)(dispatch)
      }
    },

    AUTH_STOP_IMPERSONATING: () => dispatch => {
      auth.stopImpersonating()
      dispatch(actions.authSetImpersonatedUser({}))
      if (!util.pathnameIncludes('admin')) {
        util.windowReload()
      }
    },

    AUTH_SET_PROFILE_CHANGE_IN_PROGRESS: 'profileChangeInProgress',

    AUTH_SWITCH_PROFILE: orgId => async dispatch => {
      try {
        dispatch(actions.authSetProfileChangeInProgress(true))

        const url = `${config.admin}/user/profile/switch/organization/${orgId}`
        const response = await fetch(
          url,
          auth.requests.withAuthorizationHeader({
            method: 'PUT',
          })
        )

        if (response.status !== 200) {
          throw new Error(response)
        }

        await dispatch(actions.authUser())
        global.localStorage.setItem('li.appShell.profileChanged', true)
      } catch (error) {
        alert(uhOh)(dispatch)
      } finally {
        dispatch(actions.authSetProfileChangeInProgress(false))
      }
    },

    AUTH_SHOW_PROFILE_CHANGED_NOTIFICATION: () => (dispatch, getState) => {
      const {
        auth: {
          user: { organizationName: org },
        },
      } = getState()

      alert(
        success({
          title: 'Profile Updated',
          message: `Your organization has been successfully switched to ${org}. You will only see data based on ${org}'s settings and any actions you take will be on behalf of ${org}`,
        })
      )(dispatch)
    },

    AUTH_SAFE_RENEW_TOKEN: () => dispatch => {
      if (auth.getUser() && auth.getToken()) {
        const hoursFromNow = util.millisToHours(
          new Date(auth.getTokenExpiresAt()).getTime() - Date.now()
        )

        if (hoursFromNow < 20) {
          dispatch(actions.authRenewToken())
        }
      }
    },

    AUTH_RENEW_TOKEN: () => async () => {
      try {
        await auth.renewToken(auth.getToken())
      } catch (ignore) {
        //
      }
    },
  })

  return actions
}



// WEBPACK FOOTER //
// ./~/label-insight-app-shell/src/Application/lib/auth/actions.js